import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Modal,
  Textarea,
  Group,
  Stack,
  Text,
  Anchor,
  Button,
  ActionIcon,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Competency, Swimlane, User, UserCompetency } from '../../types';
import { showToast } from '../../utils/toast';
import { getErrorMessage } from '../../utils/api';
import {
  useCreateReviewRequest,
  useReviewerPreview,
  useReviewRequests,
  useDeleteReviewRequest,
  useUsersWithCompetency,
  useCourses,
  useGrantCompetency,
  useRevokeCompetency,
  useUser,
} from '../../hooks/useApi';
import { formatDateShort } from '../../utils/date';
import { useDevice } from '../../hooks/useDevice';
import { usePermission } from '../../hooks/usePermission';
import { useUserStore } from '../../store/userStore';
import { UserBadge, userColor as hashColor, userInitials as initials } from '../user/UserBadge';
import { hasCompetency } from '../../utils/courseCompetencies';

interface DrawerProps {
  drawer: { comp: Competency; gradeIdx: number; swimlane: Swimlane } | null;
  onClose: () => void;
  grades: string[];
  currentUser?: User;
  userCompetencies?: UserCompetency[];
  competencyMapId?: string;
  hideStatusAndActions?: boolean;
}

// Local Avatar — only used inside chips/cards where we explicitly want the
// initials-only avatar without name+role. For "avatar + name + role" use
// `UserBadge` from `components/user/UserBadge`.

const Avatar: React.FC<{
  name: string;
  src?: string;
  size?: number;
  tone?: string;
}> = ({ name, src, size = 32, tone }) => {
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        style={{
          width: size,
          height: size,
          borderRadius: 999,
          objectFit: 'cover',
          flexShrink: 0,
        }}
      />
    );
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        background: tone || hashColor(name),
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.36,
        fontWeight: 600,
        flexShrink: 0,
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.18)',
      }}
    >
      {initials(name)}
    </div>
  );
};

type PillTone = 'accent' | 'warn' | 'danger' | 'neutral' | 'ghost';
const Pill: React.FC<{ tone?: PillTone; size?: 'sm' | 'md'; children: React.ReactNode }> = ({
  tone = 'neutral',
  size = 'md',
  children,
}) => {
  const map: Record<PillTone, { bg: string; fg: string; bd: string }> = {
    accent: { bg: 'var(--cd-accent-soft)', fg: 'var(--cd-accent-deep)', bd: 'transparent' },
    warn: { bg: 'var(--cd-warn-soft)', fg: 'var(--cd-warn)', bd: 'transparent' },
    danger: { bg: 'var(--cd-danger-soft)', fg: 'var(--cd-danger)', bd: 'transparent' },
    neutral: { bg: 'var(--cd-surface-2)', fg: 'var(--cd-ink-2)', bd: 'var(--cd-line)' },
    ghost: { bg: 'transparent', fg: 'var(--cd-muted)', bd: 'var(--cd-line)' },
  };
  const tt = map[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: size === 'sm' ? '3px 9px' : '5px 11px',
        background: tt.bg,
        color: tt.fg,
        border: '1px solid ' + tt.bd,
        borderRadius: 999,
        fontSize: size === 'sm' ? 11 : 12,
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
};

const Dot: React.FC<{ color?: string; size?: number }> = ({
  color = 'var(--cd-muted)',
  size = 5,
}) => (
  <span
    style={{
      width: size,
      height: size,
      borderRadius: 999,
      background: color,
      display: 'inline-block',
    }}
  />
);

const Section: React.FC<{
  label?: React.ReactNode;
  action?: React.ReactNode;
  noBorder?: boolean;
  children: React.ReactNode;
}> = ({ label, action, noBorder, children }) => (
  <div style={{ padding: '20px 24px', borderTop: noBorder ? 'none' : '1px solid var(--cd-line)' }}>
    {label && (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 14,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: 'var(--cd-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            fontWeight: 600,
          }}
        >
          {label}
        </div>
        {action}
      </div>
    )}
    {children}
  </div>
);

const EmptyHint: React.FC<{
  icon: React.ReactNode;
  title: string;
  body: string;
  action?: React.ReactNode;
}> = ({ icon, title, body, action }) => (
  <div
    style={{
      display: 'flex',
      gap: 12,
      alignItems: 'flex-start',
      padding: 14,
      background: 'var(--cd-surface-2)',
      border: '1px dashed var(--cd-line-strong)',
      borderRadius: 'var(--cd-radius)',
    }}
  >
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        background: 'var(--cd-surface)',
        color: 'var(--cd-muted)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--cd-ink-2)', marginBottom: 2 }}>
        {title}
      </div>
      <div style={{ fontSize: 12, color: 'var(--cd-muted)', lineHeight: 1.5 }}>{body}</div>
      {action && <div style={{ marginTop: 8 }}>{action}</div>}
    </div>
  </div>
);

// ─── Component ──────────────────────────────────────────────────────────────
export const CompetencyDrawer: React.FC<DrawerProps> = ({
  drawer,
  onClose,
  grades,
  currentUser,
  userCompetencies = [],
  competencyMapId,
  hideStatusAndActions = false,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const deviceType = useDevice();
  const { data: allCourses = [] } = useCourses();
  const [opened, { open, close }] = useDisclosure(false);
  const [requestModalOpened, { open: openRequestModal, close: closeRequestModal }] =
    useDisclosure(false);
  const [requestComment, setRequestComment] = useState('');
  const [isGrantOpen, setIsGrantOpen] = useState(false);
  const [isShowingAllHolders, setIsShowingAllHolders] = useState(false);
  const [grantReason, setGrantReason] = useState('');

  const createReviewRequestMutation = useCreateReviewRequest();
  // Preview of who will receive this request (shown in the submit modal).
  const { data: reviewerPreview, isLoading: reviewersLoading } = useReviewerPreview(
    drawer?.comp?._id,
    requestModalOpened,
  );
  const deleteReviewRequestMutation = useDeleteReviewRequest();

  const { data: usersWithCompetency = [], isLoading: isLoadingUsers } = useUsersWithCompetency(
    drawer?.comp?._id || '',
  );

  // Resolve approver user from the stored ID — needed for the acquisition block.
  const approverId = userCompetencies.find(
    (uc) => uc.competencyId === drawer?.comp?._id,
  )?.approvedBy;
  const { data: approver } = useUser(approverId || '');

  const actor = useUserStore((s) => s.user);
  const targetUser = currentUser;
  const canGrantOnCompany = usePermission('competencies.grant');
  const grantMutation = useGrantCompetency(targetUser?._id || '');
  const revokeMutation = useRevokeCompetency(targetUser?._id || '');

  const { data: userReviewRequests = [] } = useReviewRequests(
    actor?._id ? { applicant: actor._id, status: 'pending' } : undefined,
  );

  useEffect(() => {
    if (drawer) open();
    else close();
  }, [drawer, open, close]);

  // Reset transient state whenever drawer (re-)opens for a different competency.
  useEffect(() => {
    setIsGrantOpen(false);
    setGrantReason('');
  }, [drawer?.comp?._id]);

  if (!drawer || !drawer.comp) {
    return (
      <Drawer
        opened={opened}
        onClose={() => {
          close();
          onClose();
        }}
        title=""
        position="right"
        padding={0}
        size="lg"
        offset={8}
        radius="md"
        styles={{ body: { padding: 0 } }}
      >
        <div />
      </Drawer>
    );
  }

  const { comp, gradeIdx, swimlane } = drawer;
  const grade = grades[gradeIdx];
  const mentors = comp.mentors || [];
  const userCompetency = userCompetencies.find((uc) => uc.competencyId === comp._id);
  const isAcquired = !!userCompetency;

  // Permission to grant/revoke directly from the drawer (admin or manager of target).
  const isViewingSelf = !!actor && !!targetUser && actor._id === targetUser._id;

  // Existing review request from the LOGGED-IN actor for this competency. Only on the
  // actor's own card: on a subordinate's card it would replace "Grant" with "Cancel".
  const existingReviewRequest =
    !targetUser || isViewingSelf
      ? userReviewRequests?.find(
          (r) => r.competency._id === comp._id && r.competencyMap._id === competencyMapId,
        )
      : undefined;
  // Право выдать считает сервер (competencies.grant по охвату) и кладёт в профиль человека
  // (`GET /users/:id`). Своя карта приходит из `/users/me` без поля — тогда право на компанию.
  const canGrantToTarget =
    !!actor &&
    !!targetUser &&
    (targetUser.canGrantCompetencies ?? (isViewingSelf && canGrantOnCompany));
  // Отзывает тот, кто выдаёт (решение 2026-10-23): сервер проверяет то же право.
  const canRevokeFromTarget = canGrantToTarget;

  // State machine drives the footer + status pill in header.
  // - acquired: card is granted to target
  // - pending: target submitted a review request, awaiting decision
  // - grant_idle / grant_form: actor can grant to target, no pending RR
  // - not_acquired: card not granted, actor has no admin/manager power → can submit own review
  let state: 'acquired' | 'pending' | 'grant_idle' | 'grant_form' | 'not_acquired';
  if (isAcquired) state = 'acquired';
  else if (existingReviewRequest) state = 'pending';
  else if (canGrantToTarget) state = isGrantOpen ? 'grant_form' : 'grant_idle';
  else state = 'not_acquired';

  const isGrantMode = state === 'grant_idle' || state === 'grant_form';

  // Courses linked to this competency (relation is competency._id ∈ course.competencyIds).
  const courses = (
    allCourses as Array<{
      _id: string;
      title: string;
      estimatedHours?: number;
      competencyIds?: (string | { _id: string })[];
    }>
  ).filter((c) => hasCompetency(c, comp._id));

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSubmitRequest = async () => {
    if (!competencyMapId) {
      showToast({
        title: t('common.error'),
        message: 'ID карты компетенций не указан',
        color: 'red',
      });
      return;
    }
    try {
      await createReviewRequestMutation.mutateAsync({
        competencyId: comp._id,
        competencyMapId,
        comment: requestComment,
      });
      showToast({
        title: t('competencyDrawer.request.sent.title'),
        message: t('competencyDrawer.request.sent.message'),
        color: 'teal',
      });
      closeRequestModal();
      setRequestComment('');
    } catch (error: unknown) {
      showToast({
        title: t('common.error'),
        message: getErrorMessage(error, t('messages.errorCreating')),
        color: 'red',
      });
    }
  };

  const handleCancelRequest = async () => {
    if (!existingReviewRequest) return;
    try {
      await deleteReviewRequestMutation.mutateAsync(existingReviewRequest._id);
      showToast({
        title: t('competencyDrawer.request.cancelled.title'),
        message: t('competencyDrawer.request.cancelled.message'),
        color: 'blue',
      });
    } catch (error: unknown) {
      showToast({
        title: t('common.error'),
        message: getErrorMessage(error, t('messages.errorDeleting')),
        color: 'red',
      });
    }
  };

  const handleGrantSubmit = async () => {
    if (!targetUser?._id) return;
    try {
      await grantMutation.mutateAsync({
        competencyId: comp._id,
        reason: grantReason.trim() || undefined,
      });
      showToast({
        title: t('competencyDrawer.grant.title'),
        message: t('competencyDrawer.grant.success'),
        color: 'green',
      });
      setIsGrantOpen(false);
      setGrantReason('');
    } catch (e: unknown) {
      const data = (e as { response?: { data?: { message?: string } } })?.response?.data;
      const key =
        data?.message === 'already_granted'
          ? 'competencyDrawer.grant.errorAlreadyGranted'
          : data?.message === 'forbidden' || data?.message === 'cannot_grant_self'
            ? 'competencyDrawer.grant.errorForbidden'
            : 'competencyDrawer.grant.errorGeneric';
      showToast({
        title: t('competencyDrawer.grant.title'),
        message: t(key),
        color: 'red',
      });
    }
  };

  const handleRevokeClick = () => {
    if (!targetUser?._id) return;
    modals.openConfirmModal({
      title: t('competencyDrawer.revoke.confirmTitle'),
      children: t('competencyDrawer.revoke.confirmBody', { title: comp.title }),
      labels: {
        confirm: t('competencyDrawer.revoke.confirmAccept'),
        cancel: t('competencyDrawer.revoke.confirmCancel'),
      },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await revokeMutation.mutateAsync(comp._id);
          showToast({
            title: t('competencyDrawer.revoke.confirmTitle'),
            message: t('competencyDrawer.revoke.success'),
            color: 'green',
          });
        } catch {
          showToast({
            title: t('competencyDrawer.revoke.confirmTitle'),
            message: t('competencyDrawer.revoke.errorGeneric'),
            color: 'red',
          });
        }
      },
    });
  };

  // ── Sub-renders ────────────────────────────────────────────────────────────
  const renderHeader = () => (
    <div
      style={{
        padding: '18px 24px',
        borderBottom: '1px solid var(--cd-line)',
        background: 'var(--cd-surface)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: 'var(--cd-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span>{swimlane?.title || ''}</span>
          {grade && (
            <>
              <span style={{ color: 'var(--cd-soft)' }}>·</span>
              <span style={{ color: 'var(--cd-accent-deep)' }}>{grade}</span>
            </>
          )}
        </div>
        <ActionIcon
          variant="default"
          size="lg"
          radius="md"
          onClick={() => {
            close();
            onClose();
          }}
          aria-label={t('common.close')}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </ActionIcon>
      </div>
      <h2
        style={{
          fontSize: 18,
          fontWeight: 600,
          letterSpacing: '-0.005em',
          lineHeight: 1.35,
          marginBottom: 12,
          color: 'var(--cd-ink)',
        }}
      >
        {comp.title}
      </h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {state === 'acquired' && (
          <Pill tone="accent" size="sm">
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 12l5 5L20 6" />
            </svg>
            {t('competencyDrawer.status.acquired')}
          </Pill>
        )}
        {state === 'pending' && (
          <Pill tone="warn" size="sm">
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
            {t('competencyDrawer.status.pending')}
          </Pill>
        )}
        {state === 'not_acquired' && (
          <Pill tone="ghost" size="sm">
            {t('competencyDrawer.status.notAcquired')}
          </Pill>
        )}
        {isGrantMode && (
          <Pill tone="ghost" size="sm">
            {t('competencyDrawer.status.notAcquiredYet')}
          </Pill>
        )}
        {comp.tags?.map((tag) => (
          <Pill key={tag._id} tone="ghost" size="sm">
            {tag.name}
          </Pill>
        ))}
        {userCompetency?.source && userCompetency.source !== 'review' && (
          <Pill tone="neutral" size="sm">
            {t(`profile.competencies.sourceBadge.${userCompetency.source}`)}
          </Pill>
        )}
      </div>
      {isGrantMode && targetUser && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginTop: 14,
            padding: '10px 12px',
            background: 'var(--cd-surface-2)',
            borderRadius: 'var(--cd-radius)',
            border: '1px solid var(--cd-line)',
          }}
        >
          <Avatar name={targetUser.name} src={targetUser.avatar} size={32} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: 'var(--cd-muted)' }}>
              {t('competencyDrawer.targetContext.label')}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--cd-ink)' }}>
              {targetUser.name}
              {targetUser.position && (
                <span style={{ color: 'var(--cd-muted)', fontWeight: 400 }}>
                  {' · '}
                  {targetUser.position}
                </span>
              )}
            </div>
          </div>
          {/* «Админ» — право выдавать на всю компанию, иначе руководитель человека. */}
          {canGrantOnCompany ? (
            <Pill tone="accent" size="sm">
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
              {t('competencyDrawer.targetContext.admin')}
            </Pill>
          ) : (
            <Pill tone="accent" size="sm">
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z" />
              </svg>
              {t('competencyDrawer.targetContext.manager')}
            </Pill>
          )}
        </div>
      )}
    </div>
  );

  const renderDescription = () =>
    comp.description ? (
      <Section noBorder>
        <p style={{ fontSize: 14, color: 'var(--cd-ink-2)', lineHeight: 1.6 }}>
          {comp.description}
        </p>
      </Section>
    ) : null;

  const renderAcquisition = () => {
    if (!isAcquired || !userCompetency) return null;
    return (
      <Section label={t('competencyDrawer.acquisitionInfo.title')}>
        <div
          style={{
            padding: 16,
            background: 'var(--cd-accent-soft)',
            border: '1px solid transparent',
            borderRadius: 'var(--cd-radius)',
            color: 'var(--cd-ink-2)',
          }}
        >
          {approver && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--cd-muted)', marginBottom: 6 }}>
                {t('competencyDrawer.acquisitionInfo.confirmedBy')}
              </div>
              <UserBadge user={approver} size={36} />
            </div>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 12,
              color: 'var(--cd-ink-2)',
              ...(approver ? { paddingTop: 12, borderTop: '1px solid rgba(0,0,0,0.06)' } : {}),
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: 'var(--cd-accent-deep)' }}
            >
              <rect x="3" y="5" width="18" height="16" rx="2" />
              <path d="M3 10h18M8 3v4M16 3v4" />
            </svg>
            <span className="num" style={{ fontWeight: 500 }}>
              {formatDateShort(userCompetency.acquiredDate, i18n.language)}
            </span>
            {userCompetency.grantReason && (
              <>
                <Dot color="var(--cd-soft)" size={3} />
                <span style={{ color: 'var(--cd-muted)' }}>{userCompetency.grantReason}</span>
              </>
            )}
          </div>
        </div>
      </Section>
    );
  };

  const renderHolders = () => {
    if (isLoadingUsers) {
      return (
        <Section label={t('competencyDrawer.whoHas.title')}>
          <Text size="sm" c="dimmed">
            {t('competencyDrawer.whoHas.loading')}
          </Text>
        </Section>
      );
    }
    if (usersWithCompetency.length === 0) {
      return (
        <Section label={t('competencyDrawer.whoHas.title')}>
          <EmptyHint
            icon={
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="9" cy="8" r="3.5" />
                <path d="M2 20c0-3.5 3-6 7-6s7 2.5 7 6" />
                <path d="M16 11a3 3 0 0 0 0-6" />
              </svg>
            }
            title={t('competencyDrawer.whoHas.emptyTitle')}
            body={t('competencyDrawer.whoHas.noOne')}
          />
        </Section>
      );
    }
    return (
      <Section
        label={t('competencyDrawer.whoHas.title')}
        action={
          usersWithCompetency.length > 3 ? (
            <Anchor
              component="button"
              type="button"
              onClick={() => setIsShowingAllHolders((v) => !v)}
              style={{ fontSize: 12, color: 'var(--cd-accent-deep)', fontWeight: 500 }}
            >
              {isShowingAllHolders
                ? t('competencyDrawer.whoHas.collapse')
                : t('competencyDrawer.whoHas.allCount', { count: usersWithCompetency.length })}
            </Anchor>
          ) : null
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {(isShowingAllHolders ? usersWithCompetency : usersWithCompetency.slice(0, 3)).map(
            (u: User) => (
              <div
                key={u._id}
                style={{
                  padding: '8px 10px',
                  borderRadius: 8,
                }}
              >
                <UserBadge user={u} size={28} />
              </div>
            ),
          )}
        </div>
      </Section>
    );
  };

  const renderMentors = () => {
    if (mentors.length === 0) {
      return (
        <Section label={t('competencyDrawer.whoCanAccept.title')}>
          <EmptyHint
            icon={
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z" />
              </svg>
            }
            title={t('competencyDrawer.whoCanAccept.emptyTitle')}
            body={t('competencyDrawer.whoCanAccept.emptyBody')}
          />
        </Section>
      );
    }
    return (
      <Section label={t('competencyDrawer.whoCanAccept.title')}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {mentors.map((mentor) => {
            const m =
              typeof mentor === 'string'
                ? { _id: mentor, name: t('competencyDrawer.whoCanAccept.unknownMentor') }
                : mentor;
            return (
              <span
                key={m._id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '4px 12px 4px 4px',
                  background: 'var(--cd-surface-2)',
                  borderRadius: 999,
                  fontSize: 12,
                  color: 'var(--cd-ink-2)',
                  border: '1px solid var(--cd-line)',
                }}
              >
                <Avatar
                  name={m.name}
                  src={typeof mentor === 'object' ? mentor.avatar : undefined}
                  size={22}
                />
                {m.name}
              </span>
            );
          })}
        </div>
      </Section>
    );
  };

  const renderCourses = () => {
    if (courses.length === 0) return null;
    return (
      <Section label={t('competencyDrawer.recommendedCourses.title')}>
        <Stack gap="xs">
          {courses.map((c) => (
            <UnstyledButton
              key={c._id}
              onClick={() => {
                close();
                onClose();
                navigate(`/learning/${c._id}`);
              }}
              style={{
                display: 'grid',
                gridTemplateColumns: '36px 1fr auto',
                gap: 12,
                alignItems: 'center',
                padding: '12px 14px',
                background: 'var(--cd-surface)',
                border: '1px solid var(--cd-line)',
                borderRadius: 'var(--cd-radius)',
                color: 'inherit',
                textAlign: 'left',
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  background: 'var(--cd-surface-2)',
                  color: 'var(--cd-ink-2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 4h7a3 3 0 0 1 3 3v13a2 2 0 0 0-2-2H4z" />
                  <path d="M20 4h-7a3 3 0 0 0-3 3v13a2 2 0 0 1 2-2h8z" />
                </svg>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--cd-ink)' }}>
                  {c.title}
                </div>
                {typeof c.estimatedHours === 'number' && (
                  <div style={{ fontSize: 11, color: 'var(--cd-muted)', marginTop: 2 }}>
                    {t('learning.estimatedHours', {
                      hours: c.estimatedHours,
                    })}
                  </div>
                )}
              </div>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: 'var(--cd-soft)' }}
              >
                <path d="M5 12h14" />
                <path d="M13 6l6 6-6 6" />
              </svg>
            </UnstyledButton>
          ))}
        </Stack>
      </Section>
    );
  };

  const renderFooter = () => {
    if (hideStatusAndActions && !canGrantToTarget && !canRevokeFromTarget) return null;

    // Acquired + admin/manager-of-target → revoke option.
    if (state === 'acquired') {
      if (!canRevokeFromTarget) return null;
      return (
        <div
          style={{
            padding: '14px 24px',
            borderTop: '1px solid var(--cd-line)',
            background: 'var(--cd-surface)',
          }}
        >
          <Button
            color="red"
            variant="light"
            fullWidth
            loading={revokeMutation.isPending}
            onClick={handleRevokeClick}
            leftSection={
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              </svg>
            }
          >
            {t('competencyDrawer.revoke.button')}
          </Button>
        </div>
      );
    }

    if (state === 'pending') {
      return (
        <div
          style={{
            padding: '14px 24px',
            borderTop: '1px solid var(--cd-line)',
            background: 'var(--cd-surface)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 14px',
              background: 'var(--cd-warn-soft)',
              color: 'var(--cd-warn)',
              borderRadius: 'var(--cd-radius)',
              fontSize: 12,
              marginBottom: 10,
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
            {t('competencyDrawer.request.alreadySubmitted')}
          </div>
          <Button
            variant="default"
            fullWidth
            loading={deleteReviewRequestMutation.isPending}
            onClick={handleCancelRequest}
          >
            {t('competencyDrawer.request.cancel')}
          </Button>
        </div>
      );
    }

    if (state === 'grant_idle') {
      return (
        <div
          style={{
            padding: '14px 24px',
            borderTop: '1px solid var(--cd-line)',
            background: 'var(--cd-surface)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 11,
              color: 'var(--cd-muted)',
              marginBottom: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              fontWeight: 600,
            }}
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z" />
            </svg>
            {t('competencyDrawer.grantActions.title')}
          </div>
          {/* Админ на своей карточке: обычный путь через проверку — основной,
              самовыдача (бэкенд разрешает её админу) остаётся запасным действием. */}
          {isViewingSelf && !hideStatusAndActions && (
            <Button fullWidth size="md" mb={10} onClick={openRequestModal}>
              {t('competencyDrawer.submitForReview')}
            </Button>
          )}
          <Button
            fullWidth
            variant={isViewingSelf ? 'subtle' : 'light'}
            onClick={() => setIsGrantOpen(true)}
            leftSection={
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
            }
          >
            {isViewingSelf
              ? t('competencyDrawer.grant.selfButton')
              : t('competencyDrawer.grant.button')}
          </Button>
          {targetUser && !isViewingSelf && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--cd-muted)',
                textAlign: 'center',
                marginTop: 8,
              }}
            >
              {t('competencyDrawer.grant.emailNotice', {
                name: targetUser.name,
              })}
            </div>
          )}
        </div>
      );
    }

    if (state === 'grant_form') {
      return (
        <div
          style={{
            padding: '14px 24px',
            borderTop: '1px solid var(--cd-line)',
            background: 'var(--cd-surface)',
          }}
        >
          <div
            style={{
              padding: 14,
              background: 'var(--cd-accent-soft)',
              borderRadius: 'var(--cd-radius)',
            }}
          >
            {targetUser && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <Avatar name={targetUser.name} size={28} />
                <div style={{ flex: 1, fontSize: 13, color: 'var(--cd-ink)' }}>
                  {t('competencyDrawer.grant.confirmText', {
                    name: targetUser.name,
                  })}
                </div>
              </div>
            )}
            <label
              style={{
                display: 'block',
                fontSize: 11,
                color: 'var(--cd-accent-deep)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: 6,
              }}
            >
              {t('competencyDrawer.grant.reasonLabel')}
            </label>
            <textarea
              value={grantReason}
              onChange={(e) => setGrantReason(e.target.value)}
              placeholder={t('competencyDrawer.grant.reasonPlaceholder')}
              maxLength={500}
              rows={3}
              style={{
                width: '100%',
                padding: 10,
                fontSize: 13,
                fontFamily: 'inherit',
                background: 'var(--cd-surface)',
                color: 'var(--cd-ink)',
                border: '1px solid var(--cd-line-strong)',
                borderRadius: 6,
                outline: 'none',
                resize: 'vertical',
                lineHeight: 1.5,
              }}
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: 10,
                gap: 10,
              }}
            >
              <span className="num" style={{ fontSize: 11, color: 'var(--cd-muted)' }}>
                {grantReason.length} / 500
              </span>
              <Group gap="xs">
                <Button
                  size="xs"
                  variant="subtle"
                  color="gray"
                  onClick={() => {
                    setIsGrantOpen(false);
                    setGrantReason('');
                  }}
                  disabled={grantMutation.isPending}
                >
                  {t('competencyDrawer.grant.cancel')}
                </Button>
                <Button
                  size="xs"
                  variant="light"
                  loading={grantMutation.isPending}
                  onClick={handleGrantSubmit}
                  leftSection={
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 12l5 5L20 6" />
                    </svg>
                  }
                >
                  {t('competencyDrawer.grant.submit')}
                </Button>
              </Group>
            </div>
          </div>
        </div>
      );
    }

    // not_acquired (self-view, can submit for review). Reviewers are auto-routed,
    // so the action is available regardless of any manual mentor list.
    if (hideStatusAndActions) return null;
    // Заявку подают только за себя: на чужой карточке она ушла бы от имени смотрящего.
    if (targetUser && !isViewingSelf) return null;
    return (
      <div
        style={{
          padding: '14px 24px',
          borderTop: '1px solid var(--cd-line)',
          background: 'var(--cd-surface)',
        }}
      >
        <Button
          fullWidth
          size="md"
          onClick={openRequestModal}
          leftSection={
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 11l18-8-8 18-2-8z" />
            </svg>
          }
        >
          {t('competencyDrawer.submitForReview')}
        </Button>
      </div>
    );
  };

  return (
    <>
      <Drawer
        opened={opened}
        onClose={() => {
          close();
          onClose();
        }}
        position="right"
        withCloseButton={false}
        padding={0}
        size={deviceType === 'mobile' ? '100%' : 560}
        offset={deviceType === 'mobile' ? 0 : 8}
        radius={deviceType === 'mobile' ? 0 : 'md'}
        styles={{
          body: {
            padding: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
          content: { background: 'var(--cd-bg)' },
        }}
        classNames={{ content: 'competency-drawer-v2' }}
      >
        {renderHeader()}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {renderDescription()}
          {renderAcquisition()}
          {renderHolders()}
          {renderMentors()}
          {renderCourses()}
        </div>
        {renderFooter()}
      </Drawer>

      {/* Submit-for-review modal (kept from previous version) */}
      <Modal
        opened={requestModalOpened}
        onClose={closeRequestModal}
        title={t('competencyDrawer.requestModal.title')}
        size="md"
        centered
      >
        <Stack gap="md">
          <Text size="xs" c="dimmed">
            {t('competencyDrawer.requestModal.description', { competency: comp.title })}
          </Text>
          <div>
            <Text size="sm" fw={500} mb={6}>
              {t('competencyDrawer.requestModal.reviewers')}
            </Text>
            {reviewersLoading ? (
              <Text size="xs" c="dimmed">
                {t('common.loading')}
              </Text>
            ) : reviewerPreview && reviewerPreview.reviewers.length > 0 ? (
              <Stack gap={6}>
                {reviewerPreview.reviewers.map((r) => (
                  <Group key={r._id} gap="xs" wrap="nowrap">
                    <Avatar name={r.name} src={r.avatar} size={24} />
                    <Text size="sm">{r.name}</Text>
                  </Group>
                ))}
              </Stack>
            ) : (
              <Text size="xs" c="dimmed">
                {t('competencyDrawer.requestModal.noReviewers')}
              </Text>
            )}
          </div>
          <Textarea
            label={t('competencyDrawer.requestModal.comment.label')}
            placeholder={t('competencyDrawer.requestModal.comment.placeholder')}
            value={requestComment}
            onChange={(e) => setRequestComment(e.currentTarget.value)}
            minRows={3}
            rows={5}
            autosize
          />
          <Group justify="flex-end">
            <Button variant="subtle" color="gray" onClick={closeRequestModal}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSubmitRequest} loading={createReviewRequestMutation.isPending}>
              {t('competencyDrawer.submitForReview')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
